import React from 'react';
import { Users, Leaf, Scale, Heart, TrendingUp, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Theme {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  commitments: string[];
  color: string;
  bgColor: string;
}

const themes: Theme[] = [
  {
    icon: <Users className="w-8 h-8" />,
    title: 'COVID-19 Recovery & Economic Equality',
    subtitle: 'Creating opportunities and supporting local communities',
    commitments: [
      '2 apprenticeships per £100k contract',
      '50% recruitment from economically disadvantaged areas',
      'Living Wage employer with 30-day payment terms',
      'Skills training programs for local workforce'
    ],
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10'
  },
  {
    icon: <Leaf className="w-8 h-8" />,
    title: 'Fighting Climate Change',
    subtitle: 'Net Zero pathway with measurable carbon reduction',
    commitments: [
      'Carbon footprint tracking per £100k contract',
      'Net Zero pathway by 2030',
      'Remote-first operations reducing travel emissions',
      'Green UK datacentres powered by renewable energy'
    ],
    color: 'text-green-400',
    bgColor: 'bg-green-500/10'
  },
  {
    icon: <Scale className="w-8 h-8" />,
    title: 'Equal Opportunity',
    subtitle: 'Inclusive recruitment and accessible services',
    commitments: [
      '35% underrepresented groups in workforce',
      'WCAG 2.1 AAA accessibility in all deliverables',
      'Inclusive recruitment practices certified',
      'Diversity and inclusion training mandatory'
    ],
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10'
  },
  {
    icon: <Heart className="w-8 h-8" />,
    title: 'Wellbeing',
    subtitle: 'Supporting employee and community wellbeing',
    commitments: [
      'Flexible working arrangements for all staff',
      'Mental health support programs',
      '40 hours annual volunteering per employee',
      'Work-life balance initiatives'
    ],
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10'
  }
];

export default function SocialValueSection() {
  return (
    <div className="py-24 bg-gradient-to-b from-gray-800 via-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-teal-500/10 border border-teal-500/30 rounded-full px-4 py-2 mb-6">
            <TrendingUp className="w-4 h-4 text-teal-400" />
            <span className="text-teal-400 text-sm font-medium">Minimum 10% of tender scoring criteria</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Social Value Commitments
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Measurable commitments aligned with government procurement priorities.
            All commitments tracked quarterly with transparent KPI reporting.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {themes.map((theme, index) => (
            <div
              key={index}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 hover:border-gray-600/50 transition-all duration-300"
            >
              <div className={`${theme.bgColor} rounded-lg p-4 inline-flex mb-4`}>
                <div className={theme.color}>
                  {theme.icon}
                </div>
              </div>

              <h3 className="text-2xl font-bold text-white mb-2">
                {theme.title}
              </h3>
              <p className="text-gray-400 mb-6">
                {theme.subtitle}
              </p>

              <div className="space-y-3">
                {theme.commitments.map((commitment, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <CheckCircle className={`w-5 h-5 ${theme.color} flex-shrink-0 mt-0.5`} />
                    <span className="text-gray-300">{commitment}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">100%</div>
              <p className="text-gray-300">Commitments with measurable KPIs</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-400 mb-2">Quarterly</div>
              <p className="text-gray-300">Transparent reporting schedule</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-400 mb-2">Verified</div>
              <p className="text-gray-300">Third-party audited metrics</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            How We Track Social Value
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-teal-400 mb-3">Reporting Process</h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Quarterly KPI reports provided to all clients</li>
                <li>• Annual social value impact statement</li>
                <li>• Real-time dashboard access for active projects</li>
                <li>• Independent third-party audits</li>
                <li>• Alignment with PPN 06/20 guidance</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-blue-400 mb-3">Key Performance Indicators</h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Apprenticeships created per contract value</li>
                <li>• Local recruitment percentage achieved</li>
                <li>• Carbon footprint reduction metrics</li>
                <li>• Diversity and inclusion statistics</li>
                <li>• Community volunteering hours delivered</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/about"
            className="inline-flex items-center space-x-2 text-teal-400 hover:text-teal-300 font-semibold"
          >
            <span>Learn More About Our Values</span>
            <CheckCircle className="w-5 h-5" />
          </Link>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Our social value commitments are designed to meet and exceed the National Procurement Policy Statement requirements.
            All metrics are tracked and reported in accordance with PPN 06/20 guidance and the Social Value Act 2012.
          </p>
        </div>
      </div>
    </div>
  );
}
